import React, { useState, useEffect } from 'react';
import {
  Briefcase, Pencil, ArrowRight, FileText, Trash2,
  CalendarDays, File, User, UserMinus, Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../../utils/api';
import { API_ENDPOINTS } from '../../../../../config';
import { LeftTabKey } from '../EditJobModal';

interface TimelineEvent {
  uid: string;
  job_uid: string;
  activity: string;
  activity_type: string;
  activity_description: string;
  entity_type: string | null;
  entity_uid: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface TimelineResponse {
  events: TimelineEvent[];
  total_count: number;
  is_limited: boolean;
}

interface TimelineTabProps {
  jobId?: string;
  onNavigateTo: (entityType: string, entityUid: string | null, leftTab: LeftTabKey) => void;
}

type EventConfig = {
  Icon: React.ElementType;
  leftTab: LeftTabKey | null;
  tone?: 'muted';
};

const EVENT_CONFIG: Record<string, EventConfig> = {
  job_created:          { Icon: Briefcase,    leftTab: 'edit' },
  job_field_updated:    { Icon: Pencil,       leftTab: 'edit' },
  job_swimlane_changed: { Icon: ArrowRight,   leftTab: 'edit' },
  note_created:         { Icon: FileText,     leftTab: 'notes' },
  note_updated:         { Icon: FileText,     leftTab: 'notes' },
  note_deleted:         { Icon: Trash2,       leftTab: 'notes',      tone: 'muted' },
  activity_created:     { Icon: CalendarDays, leftTab: 'activities' },
  activity_updated:     { Icon: CalendarDays, leftTab: 'activities' },
  activity_deleted:     { Icon: Trash2,       leftTab: 'activities', tone: 'muted' },
  document_uploaded:    { Icon: File,         leftTab: 'documents' },
  document_updated:     { Icon: File,         leftTab: 'documents' },
  document_deleted:     { Icon: Trash2,       leftTab: 'documents',  tone: 'muted' },
  contact_linked:       { Icon: User,         leftTab: 'contacts' },
  contact_updated:      { Icon: User,         leftTab: 'contacts' },
  contact_unlinked:     { Icon: UserMinus,    leftTab: 'contacts',   tone: 'muted' },
  ai_task_completed:    { Icon: Sparkles,     leftTab: 'documents' },
};

const formatTimeAgo = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w`;
    return `${Math.floor(diffInDays / 30)}mo`;
  } catch {
    return '';
  }
};

export default function TimelineTab({ jobId, onNavigateTo }: TimelineTabProps) {
  const navigate = useNavigate();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLimited, setIsLimited] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const data = await api.get<TimelineResponse>(API_ENDPOINTS.timelines.list(jobId));
        setEvents(data.events || []);
        setTotalCount(data.total_count || 0);
        setIsLimited(data.is_limited || false);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [jobId]);

  const handleEventClick = (event: TimelineEvent) => {
    const config = EVENT_CONFIG[event.activity_type];
    if (!config?.leftTab) return;
    onNavigateTo(event.entity_type || '', event.entity_uid, config.leftTab);
  };

  if (loading) {
    return (
      <div className="ejm-timeline">
        <div className="ejm-tl-empty">Loading...</div>
      </div>
    );
  }

  if (events.length === 0 && !isLimited) {
    return (
      <div className="ejm-timeline">
        <div className="ejm-tl-empty">No activity yet</div>
      </div>
    );
  }

  const lockedCount = totalCount > events.length ? totalCount - events.length : 0;

  return (
    <div className="ejm-timeline">
      <div className="ejm-tl-feed">
        {events.map((event, i) => {
          const config = EVENT_CONFIG[event.activity_type];
          const IconCmp = config?.Icon;
          const isMuted = config?.tone === 'muted';
          const isLast = i === events.length - 1;
          const isClickable =
            !isLimited &&
            !!config?.leftTab &&
            event.activity_type !== 'job_created' &&
            event.activity_type !== 'job_field_updated' &&
            event.activity_type !== 'job_swimlane_changed';

          return (
            <div
              key={event.uid}
              className={[
                'ejm-tl-item',
                isMuted ? 'ejm-tl-item--muted' : '',
                isClickable ? 'ejm-tl-item--clickable' : '',
              ].filter(Boolean).join(' ')}
              onClick={isClickable ? () => handleEventClick(event) : undefined}
            >
              <div className="ejm-tl-rail-col">
                <div className={`ejm-tl-marker${isMuted ? ' ejm-tl-marker--muted' : ''}`}>
                  {IconCmp && <IconCmp size={11} strokeWidth={2} />}
                </div>
                {(!isLast || isLimited) && (
                  <div
                    className={`ejm-tl-connector${
                      isLast && isLimited ? ' ejm-tl-connector--fade' : ''
                    }`}
                  />
                )}
              </div>
              <div className="ejm-tl-body">
                <div className="ejm-tl-row">
                  <span className="ejm-tl-title">{event.activity}</span>
                  <span className="ejm-tl-time">{formatTimeAgo(event.created_at)}</span>
                </div>
                <div className="ejm-tl-desc">{event.activity_description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {isLimited && (
        <div className="ejm-tl-lock">
          <div className="ejm-tl-lock-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <p className="ejm-tl-lock-text">
            {lockedCount > 0
              ? `${lockedCount} more ${lockedCount === 1 ? 'event' : 'events'} hidden`
              : 'Upgrade to view full activity timeline'}
          </p>
          <button
            type="button"
            className="ejm-tl-upgrade-btn"
            onClick={() => navigate('/settings/plan/purchase')}
          >
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
}
